import type { TurnstileInstance, TurnstileProps } from '@marsidev/react-turnstile';
import { Turnstile } from '@marsidev/react-turnstile';
import { getCookieValue } from '@naturehouse/nh-essentials/lib/cookies/cookie';
import { onIntersectionObserver } from '@naturehouse/nh-essentials/lib/events/eventHandling';
import { createRoot } from 'react-dom/client';
import React from 'react';
import { DeploymentEnvironment } from '../../../assets/js/util/featureToggle';

const shouldBypassCaptcha = (): boolean =>
    document.documentElement.dataset.deploymentEnv !== DeploymentEnvironment.PROD &&
    getCookieValue('captcha_bypass_token') !== null;

function toggleFormSubmitButton(form: HTMLFormElement, isDisabled: boolean = false): void {
    const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]');

    if (!submitButton) {
        return;
    }

    submitButton.disabled = isDisabled;
}

export function createCaptchaComponent(
    input: HTMLInputElement | null,
    { onSuccess, onError, onWidgetLoad }: Partial<TurnstileProps> = {},
    ref: React.RefObject<TurnstileInstance | null> = React.createRef<TurnstileInstance>()
): void {
    const siteKey = input?.getAttribute('data-sitekey');
    const container = input?.parentElement;
    const form = input?.closest('form');

    if (shouldBypassCaptcha() && onSuccess) {
        onSuccess('');
        return;
    }

    if (!siteKey || !container) {
        return;
    }

    const id = input.getAttribute('id') ?? undefined;
    const responseFieldName = input.getAttribute('name') ?? undefined;

    createRoot(container).render(
        <Turnstile
            className="cf-captcha"
            id={id}
            ref={ref}
            onWidgetLoad={
                onWidgetLoad ?? ((): void | null => form && toggleFormSubmitButton(form, true))
            }
            onSuccess={
                onSuccess ?? ((): void | null => form && toggleFormSubmitButton(form, false))
            }
            onError={onError}
            siteKey={siteKey}
            options={{
                appearance: 'interaction-only',
                feedbackEnabled: false,
                responseFieldName
            }}
        />
    );
}

export default function renderCaptchaComponent(): void {
    const inputs = document.querySelectorAll<HTMLInputElement>('[data-role="cf-turnstile"]');

    if (shouldBypassCaptcha()) {
        return;
    }

    for (const input of inputs) {
        const render = (): void => createCaptchaComponent(input);

        const container = input.parentElement;
        if ('IntersectionObserver' in window && container) {
            onIntersectionObserver(container, render, {
                root: null,
                rootMargin: '0px',
                threshold: 1.0
            });
            continue;
        }

        render();
    }
}
